<template>
	<div class="drip-campaign-item-contacts">
		<div class="contact-actions-container">
			<SearchInput
				ariaLabel="`Search for contacts`"
				placeholder="Search contacts..."
				@onSearchInputChange="handleSearchInputChange"
			/>
			<FilterMenu
				:filterOptions="filterOptions"
				@onFilterChange="handleFilterChange"
			/>
		</div>
		<div v-if="!error && isLoading">
			<FallBack>
				<LoadingIcon />
			</FallBack>
		</div>
		<div v-if="!error && !isLoading && noContacts">
			<FallBack iconStyle="far" icon="users">
				<p aria-label="warning or error message">
					You have no contacts.
				</p>
			</FallBack>
		</div>
		<div v-if="!error && !isLoading && !noContacts && isEmpty(contacts)">
			<FallBack iconStyle="far" icon="users">
				<p aria-label="warning or error message">
					Sorry, there are no results.
				</p>
			</FallBack>
		</div>
		<div v-if="!isEmpty(contacts) && !error && !isLoading">
			<Table class="table" :aria-label="'contacts list'">
				<thead class="table-head">
					<tr class="table-head-row">
						<th
							v-for="(columnHeader, index) in columnHeaders"
							:key="index"
							scope="col"
							@click="handleSort(columnHeader.value)"
							:class="`table-head__${columnHeader.className}`"
						>
							{{ columnHeader.name }}
							<font-awesome-icon
								v-if="
									sortDirection === 'asc' &&
									sortColumn === columnHeader.value
								"
								:icon="['fad', 'sort-down']"
								class="sort-down-icon"
							/>
							<font-awesome-icon
								v-else-if="
									sortDirection === 'desc' &&
									sortColumn === columnHeader.value
								"
								:icon="['fad', 'sort-up']"
								class="sort-up-icon"
							/>
							<font-awesome-icon v-else :icon="['fas', 'sort']" />
						</th>
					</tr>
				</thead>
				<tbody v-if="currentPageContacts" class="table-body">
					<tr
						class="table-body-row"
						v-for="(contact, index) in currentPageContacts"
						:key="index"
					>
						<td class="table-body__name">
							<TextButton
								class="text-button"
								aria-label="Full Name"
								@onClick="
									handleContactNameClick(contact.leadId)
								"
								>{{ contact.firstName }}
								{{ contact.lastName }}</TextButton
							>
						</td>
						<td class="table-body__email">
							<TextButton
								class="text-button"
								aria-label="Email"
								@onClick="
									handleContactEmailClick(contact.email)
								"
								>{{
									truncate(contact.email, { length: 36 })
								}}</TextButton
							>
						</td>
						<td class="table-body__status">
							<div class="status">
								{{ contact.status.leadStatusDesc }}
							</div>
						</td>
						<td>{{ formatDate(contact.lastCommunicationDate) }}</td>
					</tr>
				</tbody>
			</Table>
			<Pagination
				v-if="totalCount > 20 && !isLoading"
				@onPageChange="handlePageNumberChange"
				@onPageSizeChange="handlePageSizeChange"
				:totalCount="totalCount"
				:pageNumber="pageNumber"
				:pageSize="pageSize"
			/>
		</div>

		<OverviewModal
			v-if="showOverviewModal"
			@close="showOverviewModal = false"
			:leadId="leadIdClicked"
			:modalView="modalView"
		/>
	</div>
</template>

<script>
import { isEmpty, truncate, debounce } from 'lodash';
import { mapActions, mapState, mapGetters } from 'vuex';
import dripCampaignService from '@/services/toolbox/drip-campaign-service';

import FallBack from '@/components/global/FallBack.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import Pagination from '@/components/global/Pagination.vue';
import Table from '@/components/global/Table.vue';
import TextButton from '@/components/global/TextButton.vue';
import SearchInput from '@/components/global/SearchInput.vue';

import FilterMenu from './DripCampaignContactsFilterMenu';
import OverviewModal from '../modals/OverviewModal.vue';

import { scrollToTop } from '../helpers';
import { CONTACTS } from './constants.js';
import { VIEWS as OVERVIEW_MODAL_VIEWS } from '../modals/constants';

export default {
	name: 'DripCampaignItemContacts',
	components: {
		FallBack,
		LoadingIcon,
		Pagination,
		Table,
		TextButton,
		SearchInput,
		FilterMenu,
		OverviewModal,
	},
	props: {
		dripCampaignId: null,
	},
	data() {
		return {
			columnHeaders: CONTACTS.TABLE_HEADERS,
			currentPageContacts: [],
			error: null,
			filterOptions: null,
			isLoading: false,
			leadIdClicked: null,
			modalView: '',
			noContacts: false,
			pageNumber: 1,
			pageSize: 20,
			searchTerm: '',
			showOverviewModal: false,
			slicedContacts: null,
			sortColumn: 'lastName',
			sortDirection: 'asc',
			statusIds: [],
		};
	},
	computed: {
		...mapState({
			onInit: (state) => state.dripCampaigns.leads.onInit,
			contacts: (state) => state.dripCampaigns.leads.items,
			totalCount: (state) => state.dripCampaigns.leads.totalCount,
			contactId: (state) => state.profile.rolodexContactId,
		}),
		...mapGetters(['currentUserRolodexIds']),
	},
	methods: {
		...mapActions(['getDripCampaignContactLeads']),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		// deepcode ignore VueIncorrectMethodType: see comment above
		truncate,
		getCurrentPageContacts() {
			this.slicedContacts = this.contacts.slice(
				(this.pageNumber - 1) * this.pageSize,
				this.pageNumber * this.pageSize,
			);
			if (!isEmpty(this.slicedContacts)) {
				this.currentPageContacts = [...this.slicedContacts];
			}
		},
		async getContacts(bySearchTerm) {
			try {
				const payload = {
					data: {
						dripCampaignId: this.dripCampaignId,
						entityId: this.currentUserRolodexIds.entityId,
						contactIds: [this.contactId],
						statusIds: this.statusesIds,
						dateRange: 0,
					},
					searchTerm: this.searchTerm,
					sortColumn: this.sortColumn,
					sortDirection: this.sortDirection,
					pageNumber: this.pageNumber,
					pageSize: this.pageSize,
				};
				await this.getDripCampaignContactLeads({
					payload,
					bySearchTerm,
				});
			} catch (error) {
				this.error = error;
			}
		},
		handleContactEmailClick(emailAddress) {
			emailAddress.includes('@') &&
				(window.location.href = `mailto:${emailAddress}`);
		},
		handleContactNameClick(leadId) {
			this.showOverviewModal = true;
			this.leadIdClicked = leadId;
			this.modalView = OVERVIEW_MODAL_VIEWS.HOME;
		},
		async handleFilterChange(statusesIds) {
			this.statusesIds = statusesIds;
			this.isLoading = true;
			this.pageNumber = 1;
			await this.getContacts(true);
			if (!isEmpty(this.contacts)) {
				this.getCurrentPageContacts();
				this.noResultsFound = false;
			} else {
				this.noResultsFound = true;
			}
			this.isLoading = false;
		},
		handlePageNumberChange(page) {
			this.pageNumber = page;
			this.getCurrentPageContacts();
		},
		handlePageSizeChange(size) {
			this.pageSize = size;
			this.pageNumber = 1;
			scrollToTop();
			this.getCurrentPageContacts();
		},
		handleSearchInputChange(value) {
			this.searchTerm = value;
		},
		async handleSort(column) {
			if (this.sortColumn !== column) {
				this.sortColumn = column;
				this.sortDirection = 'asc';
			} else {
				this.sortDirection =
					this.sortDirection === 'asc' ? 'desc' : 'asc';
			}
			this.pageNumber = 1;
			this.isLoading = true;
			await this.getContacts(true);
			this.getCurrentPageContacts();
			this.isLoading = false;
		},
		formatDate(date) {
			const d = new Date(date);
			if (d.getFullYear() < 1900) {
				return '';
			} else {
				return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
			}
		},
	},
	watch: {
		async slicedContacts(value) {
			if (this.error) {
				return;
			}
			const lastPage = this.totalCount / this.pageSize;
			if (this.pageNumber === lastPage && !isEmpty(value)) {
				return;
			} else if (
				(isEmpty(value) || value.length !== this.pageSize) &&
				this.totalCount > this.contacts.length &&
				!this.error
			) {
				this.isLoading = true;
				await this.getContacts();
				this.getCurrentPageContacts();
				this.isLoading = false;
			}
		},
		searchTerm: debounce(async function () {
			this.isLoading = true;
			this.pageNumber = 1;
			await this.getContacts(true);
			this.isLoading = false;
			if (!isEmpty(this.contacts)) {
				this.getCurrentPageContacts();
				this.noResultsFound = false;
			} else {
				this.noResultsFound = true;
			}
		}, 500),
	},
	async created() {
		if (this.onInit) {
			this.isLoading = true;
			try {
				const response =
					await dripCampaignService.getAllDripCampaignContactStatuses(
						this.dripCampaignId,
					);
				this.filterOptions = [
					{
						type: 'Lead Status',
						data: response.data,
					},
				];
			} catch (error) {
				this.error = 'Unable to retrieve data';
			}
			await this.getContacts();
			this.isLoading = false;
			if (isEmpty(this.contacts)) {
				this.noContacts = true;
			} else {
				this.getCurrentPageContacts();
			}
		}
	},
};
</script>

<style lang="scss" scoped>
.drip-campaign-item-contacts {
	@include rem('padding', 30px 0);

	.contact-actions-container {
		@include rem('margin-bottom', 15px);

		display: grid;
		grid-template-columns: auto 44px;
		grid-gap: 15px;

		@media only screen and (max-width: 600px) {
			grid-template-rows: 1fr 1fr;
			grid-template-columns: auto 44px;
			grid-template-areas: 'contacts contacts contacts';
		}
	}

	.table {
		.table-head-row,
		.table-body-row {
			display: grid;
			grid-template-columns: 30% 30% 15% 15%;
			gap: 1%;
		}

		.sort-up-icon {
			@include rem('margin-bottom', 2px);
		}

		.sort-down-icon {
			@include rem('margin-bottom', -4px);
		}

		@media only screen and (max-width: 600px) {
			.table-head-row,
			.table-body-row {
				grid-template-columns: 50% 50%;
				gap: 1%;
			}

			.table-head__status,
			.table-body__status,
			.table-head__last,
			.table-body__last,
			.table-head__next,
			.table-body__next {
				display: none;
			}
		}

		@media only screen and (min-width: 601px) and (max-width: 1200px) {
			.table-head-row,
			.table-body-row {
				grid-template-columns: 40% 40% 20%;
				gap: 1%;
			}

			.table-head__last,
			.table-body__last,
			.table-head__next,
			.table-body__next {
				display: none;
			}
		}

		.text-button {
			background: transparent;
		}

		.status {
			font-weight: bold;
			text-transform: uppercase;
			box-sizing: border-box;
			border-radius: 6px;
			width: fit-content;
			height: fit-content;
			@include rem('font-size', 13px);
			@include rem('padding', 4px 6px);
			color: $brand-blue;
			background: rgba($brand-blue, 0.05);
			border: 1px solid $brand-blue;
		}
	}
}
</style>
